<template>
  <div class="min-h-screen bg-white">
    <!-- Filters and Products -->
    <div class="container mx-auto px-4">
      <div class="flex flex-col lg:flex-row gap-8">
        <!-- Sidebar with Filters - Visible on Desktop only -->
        <div class="hidden lg:block w-[300px] flex-shrink-0">
          <div class="sticky top-8">
            <div> <!-- Removido border border-[#FAFAFA] -->
              <!-- Category Filter -->
              <div class="flex flex-col gap-12 w-[328px]">
                <!-- Filter Title -->
                <div class="flex flex-col w-full">
                  <div class="flex items-center w-full h-[72.66px] bg-black border-b-[5px] border-b-empire-yellow transform rotate-[0.21deg]">
                    <h3 class="font-archivo-narrow font-semibold text-[34px] leading-[72px] text-empire-yellow px-6">
                      {{ $t('categoryPage.categories') }}
                    </h3>
                  </div>
                </div>

                <!-- Hierarchical Category List -->
                <div class="mb-12 category-filter-container">
                  <div v-if="loadingCategories" class="flex justify-center items-center py-4">
                    <div class="loader-container">
                      <div class="loader-spinner"></div>
                      <div class="loader-text">{{ $t('common.loading') }}...</div>
                    </div>
                  </div>
                  <CategoryTree
                    v-else
                    :categories="categoryTree"
                    :selected-category="selectedCategory"
                    @select="selectCategory"
                  />
                </div>
              </div>

                <!-- Price Filter (shown only if the master toggle is enabled) -->
                <div v-if="showPrices" class="flex flex-col gap-12 w-[328px] mb-12">
                  <!-- Filter Title -->
                  <div class="flex flex-col w-full">
                    <div class="flex items-center w-full h-[72.66px] bg-black border-b-[5px] border-b-empire-yellow transform rotate-[0.21deg]">
                      <h3 class="font-archivo-narrow font-semibold text-[34px] leading-[72px] text-empire-yellow px-6">
                        {{ $t('categoryPage.price') }}
                      </h3>
                    </div>
                  </div>

                  <!-- Filter Content -->
                  <div class="px-3">
                    <div class="flex justify-between items-center mb-2">
                      <span class="font-archivo text-sm">
                        {{ currencySymbol }}{{ priceRange[0] }}
                      </span>
                      <span class="font-archivo text-sm">
                        {{ currencySymbol }}{{ priceRange[1] }}
                      </span>
                    </div>
                    <div class="relative w-full h-8">
                      <div class="absolute w-full h-2 bg-[#E6E6E6] rounded-full top-3"></div>
                      <div
                        class="absolute h-2 bg-black rounded-full top-3"
                        :style="{ left: (priceRange[0] / maxPrice) * 100 + '%', right: 100 - (priceRange[1] / maxPrice) * 100 + '%' }"
                      ></div>
                      <input
                        type="range"
                        :min="0"
                        :max="maxPrice"
                        v-model.number="priceRange[0]"
                        class="absolute w-full h-2 appearance-none bg-transparent pointer-events-none"
                        @mousedown="handleRangeStart"
                        @mouseup="handleRangeEnd"
                        @touchstart="handleRangeStart"
                        @touchend="handleRangeEnd"
                      >
                      <input
                        type="range"
                        :min="0"
                        :max="maxPrice"
                        v-model.number="priceRange[1]"
                        class="absolute w-full h-2 appearance-none bg-transparent pointer-events-none"
                        @mousedown="handleRangeStart"
                        @mouseup="handleRangeEnd"
                        @touchstart="handleRangeStart"
                        @touchend="handleRangeEnd"
                      >
                    </div>
                  </div>
                </div>

                <!-- Brand Filter -->
                <div class="flex flex-col gap-12 w-[328px] h-[303.69px]"> <!-- Increased gap-6 to gap-12 -->
                  <!-- Filter Title -->
                  <div class="flex flex-col w-full">
                    <div class="flex items-center w-full h-[72.66px] bg-black border-b-[5px] border-b-empire-yellow transform rotate-[0.21deg]">
                      <h3 class="font-archivo-narrow font-semibold text-[34px] leading-[72px] text-empire-yellow px-6">
                        {{ $t('categoryPage.brands') }}
                      </h3>
                    </div>
                  </div>

                  <!-- Filter Content -->
                  <div class="border border-[#FAFAFA] transform rotate-[0.21deg]">
                    <div class="flex flex-col gap-3">
                      <label
                        v-for="brand in brands"
                        :key="brand.id"
                        class="flex items-center px-6 py-1 gap-3 h-[24.09px]"
                      >
                        <input
                          type="checkbox"
                          :value="brand.id"
                          v-model="selectedBrands"
                          class="w-6 h-6 accent-[#FBBD1E]"
                        >
                        <span class="font-archivo text-[16px] leading-[18px] text-black/70">
                          {{ brand.name }}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
            </div>
          </div>
        </div>

        <!-- Product Grid -->
        <div class="flex-1">
          <!-- Mobile Filters and Sorting -->
          <div class="flex justify-between items-center p-3 border-2 border-black w-full h-16 mb-6">
            <div class="flex-1 flex items-center">
              <!-- Mobile Filter Button -->
              <button
                @click="isMobileFiltersExpanded = !isMobileFiltersExpanded"
                class="md:hidden flex items-center justify-center w-[36px] h-[36px] bg-black p-[6px]"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 4L9 12V18L15 21V12L20 4H4Z"
                    stroke="#FFDD00"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>

              <!-- Results text - Visible on desktop only -->
              <span class="font-inter text-base hidden md:block">
                {{ totalItems === 0
                  ? $t('categoryPage.noResults')
                  : $t('categoryPage.itemsCount', {
                      start: itemRange.start,
                      end: itemRange.end,
                      total: totalItems
                    })
                }}
              </span>
            </div>

            <div class="flex justify-end w-[240px]">
              <div class="relative w-full">
                <select
                  v-model="sortBy"
                  class="w-full h-10 px-4 py-2 bg-white border border-[#D9D9D9] font-inter text-sm appearance-none cursor-pointer"
                  @change="handleSort"
                >
                  <option
                    v-for="option in sortOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ $t(option.label) }}
                  </option>
                </select>
                <div class="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none">
                  <svg class="w-4 h-4" viewBox="0 0 24 24" fill="#FFDD00">
                    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <!-- Expandable Mobile Filters Section -->
          <div
            v-show="isMobileFiltersExpanded"
            class="w-full mb-6 transition-all duration-300"
          >
            <!-- Category Filter -->
            <div class="mb-6">
              <div @click="isCategoryExpanded = !isCategoryExpanded" class="flex items-center justify-between w-full h-[50px] md:h-[72.66px] bg-black border-b-[3px] md:border-b-[5px] border-b-empire-yellow cursor-pointer md:cursor-default">
                <h3 class="font-archivo-narrow font-semibold text-[20px] md:text-[34px] leading-[50px] md:leading-[72px] text-empire-yellow px-4 md:px-6">
                  {{ $t('categoryPage.categories') }}
                </h3>
                <!-- Yellow arrow to expand/collapse (mobile only) -->
                <div class="md:hidden flex items-center pr-4">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" :class="{ 'transform rotate-180': !isCategoryExpanded }">
                    <path d="M6 9L12 15L18 9" stroke="#FFDD00" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
              <div v-show="isCategoryExpanded" class="category-filter-container-mobile">
                <div v-if="loadingCategories" class="flex justify-center items-center py-4">
                  <div class="loader-container">
                    <div class="loader-spinner"></div>
                    <div class="loader-text">{{ $t('common.loading') }}...</div>
                  </div>
                </div>
                <CategoryTree
                  v-else
                  :categories="categoryTree"
                  :selected-category="selectedCategory"
                  @select="selectCategory"
                />
              </div>
            </div>

            <!-- Price Filter (shown only if the master toggle is enabled) -->
            <div v-if="showPrices" class="mb-6">
              <div @click="isPriceExpanded = !isPriceExpanded" class="flex items-center justify-between w-full h-[50px] md:h-[72.66px] bg-black border-b-[3px] md:border-b-[5px] border-b-empire-yellow cursor-pointer md:cursor-default">
                <h3 class="font-archivo-narrow font-semibold text-[20px] md:text-[34px] leading-[50px] md:leading-[72px] text-empire-yellow px-4 md:px-6">
                  {{ $t('categoryPage.price') }}
                </h3>
                <!-- Yellow arrow to expand/collapse (mobile only) -->
                <div class="md:hidden flex items-center pr-4">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" :class="{ 'transform rotate-180': !isPriceExpanded }">
                    <path d="M6 9L12 15L18 9" stroke="#FFDD00" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
              <div v-show="isPriceExpanded" class="border border-[#FAFAFA] p-4">
                <div class="flex justify-between items-center mb-2">
                  <span class="font-archivo text-sm">
                    {{ currencySymbol }}{{ priceRange[0] }}
                  </span>
                  <span class="font-archivo text-sm">
                    {{ currencySymbol }}{{ priceRange[1] }}
                  </span>
                </div>
                <div class="relative w-full h-8">
                  <div class="absolute w-full h-2 bg-[#E6E6E6] rounded-full top-3"></div>
                  <div
                    class="absolute h-2 bg-black rounded-full top-3"
                    :style="{ left: (priceRange[0] / maxPrice) * 100 + '%', right: 100 - (priceRange[1] / maxPrice) * 100 + '%' }"
                  ></div>
                  <input
                    type="range"
                    :min="0"
                    :max="maxPrice"
                    v-model.number="priceRange[0]"
                    class="absolute w-full h-2 appearance-none bg-transparent pointer-events-none"
                    @mousedown="handleRangeStart"
                    @mouseup="handleRangeEnd"
                    @touchstart="handleRangeStart"
                    @touchend="handleRangeEnd"
                  >
                  <input
                    type="range"
                    :min="0"
                    :max="maxPrice"
                    v-model.number="priceRange[1]"
                    class="absolute w-full h-2 appearance-none bg-transparent pointer-events-none"
                    @mousedown="handleRangeStart"
                    @mouseup="handleRangeEnd"
                    @touchstart="handleRangeStart"
                    @touchend="handleRangeEnd"
                  >
                </div>
              </div>
            </div>

            <!-- Brand Filter -->
            <div class="mb-6">
              <div @click="isBrandsExpanded = !isBrandsExpanded" class="flex items-center justify-between w-full h-[50px] md:h-[72.66px] bg-black border-b-[3px] md:border-b-[5px] border-b-empire-yellow cursor-pointer md:cursor-default">
                <h3 class="font-archivo-narrow font-semibold text-[20px] md:text-[34px] leading-[50px] md:leading-[72px] text-empire-yellow px-4 md:px-6">
                  {{ $t('categoryPage.brands') }}
                </h3>
                <!-- Yellow arrow to expand/collapse (mobile only) -->
                <div class="md:hidden flex items-center pr-4">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" :class="{ 'transform rotate-180': !isBrandsExpanded }">
                    <path d="M6 9L12 15L18 9" stroke="#FFDD00" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
              <div v-show="isBrandsExpanded" class="border border-[#FAFAFA] p-4">
                <div class="flex flex-col gap-3">
                  <label
                    v-for="brand in brands"
                    :key="brand.id"
                    class="flex items-center gap-3 h-[24.09px]"
                  >
                    <input
                      type="checkbox"
                      :value="brand.id"
                      v-model="selectedBrands"
                      class="w-6 h-6 accent-[#FBBD1E]"
                    >
                    <span class="font-archivo text-[20px] leading-[22px] text-black/70">
                      {{ brand.name }}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Product Grid -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0.5 md:gap-1">
            <div v-if="loading" class="col-span-full flex justify-center items-center py-12">
              <div class="loader-container">
                <div class="loader-spinner"></div>
                <div class="loader-text">{{ $t('common.loading') }}...</div>
              </div>
            </div>
            <div v-else-if="error" class="col-span-full text-red-500">
              {{ error }}
            </div>
            <template v-else>

              <div
                v-for="product in filteredProducts"
                :key="product.id"
                :class="[
                  'flex flex-col bg-white border border-[#FAFAFA]',
                  showPrices ? 'min-h-[250px] md:min-h-[280px] md:h-[300px]' : 'min-h-[230px] md:min-h-[260px] md:h-[280px]'
                ]"
              >
                <!-- Wrap the clickable area in a div -->
                <div
                  class="flex flex-col flex-grow cursor-pointer"
                  @click="navigateToProduct(product.id)"
                >
                  <img
                    :src="getProductImage(product)"
                    :alt="product.name"
                    class="w-[90%] h-[110px] md:h-[140px] object-contain object-center mx-auto pt-1 md:pt-0"
                    @error="handleImageError"
                  >
                  <div class="p-1 pt-1 pb-0 md:p-1 md:pb-0 flex flex-col h-full">
                    <!-- Product description in bold (swapped with the name) -->
                    <div class="h-[32px] md:h-[36px] overflow-hidden">
                      <p class="font-archivo font-bold text-[12px] md:text-[13px] leading-[16px] md:leading-[18px] text-black/90 line-clamp-2 mb-0 text-center">
                        {{ product.description || '-' }}
                      </p>
                    </div>
                    <!-- Product name with a lighter font -->
                    <h3 class="font-archivo-narrow text-[12px] md:text-[14px] leading-[14px] md:leading-[16px] line-clamp-1 truncate mt-0 mb-0 uppercase product-name">
                      {{ product.name }}
                    </h3>
                    <div class="mt-2 w-full mb-0 pb-0">
                      <!-- When showPrices is true, show the price with proper spacing -->
                      <p v-if="showPrices" class="font-archivo-narrow font-semibold text-[18px] md:text-[20px] leading-[20px] md:leading-[22px] mt-0 mb-0">
                        {{ formatPrice(product.price) }}
                      </p>
                      <!-- When showPrices is false, do not add spacing -->
                      <div v-else class="mt-0"></div>
                      <!-- Wrap the button in a div that stops event propagation -->
                      <div @click.stop class="mt-0 -mb-1">
                        <ProductQuantitySelector
                          @add-to-cart="(quantity) => handleAddToCart(product, quantity)"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <!-- Pagination -->
          <div class="flex flex-col items-center mt-12 mb-24 w-full gap-4">
            <div class="flex justify-center items-center gap-2 md:gap-4 w-full">
              <!-- Previous Button -->
              <button
                class="flex items-center justify-center h-10 px-2 md:px-4 gap-1 bg-[#F9F9FB] rounded-lg min-w-[90px] md:min-w-[120px]"
                :disabled="currentPage === 1"
                @click="handlePageChange(currentPage - 1)"
              >
                <svg class="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.5 15L7.5 10L12.5 5" stroke="#1E1E1E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span class="font-inter font-medium text-sm md:text-base">{{ $t('categoryPage.previous') }}</span>
              </button>

              <!-- Page Numbers -->
              <div class="flex gap-1 md:gap-2">
                <button
                  v-for="page in displayedPages"
                  :key="page"
                  class="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-lg text-sm md:text-base"
                  :class="page === currentPage ? 'bg-black text-white' : 'bg-[#F9F9FB] text-black'"
                  @click="handlePageChange(page)"
                >
                  {{ page }}
                </button>
              </div>

              <!-- Next Button -->
              <button
                class="flex items-center justify-center h-10 px-2 md:px-4 gap-1 bg-[#F9F9FB] rounded-lg min-w-[90px] md:min-w-[120px]"
                :disabled="currentPage === totalPages"
                @click="handlePageChange(currentPage + 1)"
              >
                <span class="font-inter font-medium text-sm md:text-base">{{ $t('categoryPage.next') }}</span>
                <svg class="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.5 15L12.5 10L7.5 5" stroke="#1E1E1E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>

            <!-- Item counter in the pagination -->
            <div class="flex justify-center">
              <span class="font-inter text-sm md:text-base">
                {{ totalItems === 0
                  ? $t('categoryPage.noResults')
                  : $t('categoryPage.itemsCount', {
                      start: itemRange.start,
                      end: itemRange.end,
                      total: totalItems
                    })
                }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Filters Modal -->
    <div
      v-if="showMobileFilters"
      class="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden"
      @click="showMobileFilters = false"
    >
      <div
        class="absolute right-0 top-0 bottom-0 w-[300px] bg-white p-4 overflow-y-auto"
        @click.stop
      >
        <div class="flex justify-between items-center mb-3">
          <h2 class="font-archivo-narrow font-semibold text-[18px] leading-[22px]">{{ $t('categoryPage.filters') }}</h2>
          <button
            @click="showMobileFilters = false"
            class="p-2"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="#000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <!-- Mobile Filters Content -->
        <div class="space-y-2">
          <!-- Price Filter (visible only when showPrices is true) -->
          <div v-if="showPrices">
            <h3 class="font-archivo-narrow font-semibold text-sm mb-1">{{ $t('categoryPage.priceRange') }}</h3>
            <div class="space-y-2">
              <!-- Price slider using the native range input -->
              <div class="px-1 py-2">
                <div class="mb-2 flex justify-between">
                  <span class="font-archivo text-sm">{{ showPrices ? `${currencySymbol}${priceRange[0]}` : '' }}</span>
                  <span class="font-archivo text-sm">{{ showPrices ? `${currencySymbol}${priceRange[1]}` : '' }}</span>
                </div>
                <div class="relative h-8 mt-2 mb-2">
                  <input
                    type="range"
                    :min="0"
                    :max="maxPrice"
                    :value="priceRange[1]"
                    @input="updateMaxPriceSlider"
                    @change="handlePriceRangeChange"
                    class="absolute w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    style="z-index: 1;"
                  />
                  <input
                    type="range"
                    :min="0"
                    :max="maxPrice"
                    :value="priceRange[0]"
                    @input="updateMinPrice"
                    @change="handlePriceRangeChange"
                    class="absolute w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    style="z-index: 2;"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Brand Filter -->
          <div>
            <h3 class="font-archivo-narrow font-semibold text-sm mb-1">{{ $t('categoryPage.brand') }}</h3>
            <div class="space-y-1">
              <label v-for="brand in brands" :key="brand.id" class="flex items-center gap-1">
                <input
                  type="checkbox"
                  :value="brand.id"
                  v-model="selectedBrands"
                  class="w-4 h-4"
                >
                <span class="font-archivo text-sm">{{ brand.name }}</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Toast Message -->
  <div
    v-if="showToast"
    class="fixed bottom-4 right-4 bg-black text-empire-yellow px-6 py-4 rounded-md shadow-lg z-50 transition-opacity duration-300"
    :class="{ 'opacity-0': !showToast, 'opacity-100': showToast }"
  >
    <p class="font-archivo-narrow text-lg">
      {{ $t('cart.productAdded') }}
    </p>
  </div>
</template>

<style scoped>
/* Reduce spacing between the card elements */
.grid-cols-4 {
  gap: 0.5rem !important;
}

/* Style for line-clamp */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.1;
  word-break: break-word;
}

/* Custom style for the product name with extra thin weight */
.product-name {
  font-weight: 100 !important; /* Forces the thinnest possible weight */
  opacity: 0.7 !important; /* Lowers the opacity further so it looks thinner */
  letter-spacing: 0.03em !important; /* Increases the letter spacing further */
  color: rgba(0, 0, 0, 0.6) !important; /* Lighter color so it looks thinner */
  transform: scale(0.98, 1) !important; /* Comprime ligeiramente na horizontal */
  text-align: center !important; /* Centraliza o texto */
}
</style>

<script setup>
/* eslint-disable */
import { ref, onMounted, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { productService, filterProductsByDisplayedPriceRange } from '@/services/productService'
import { categoryService } from '@/services/categoryService'
import { settingsService } from '@/services/settingsService'
import { imageService } from '@/services/imageService'
import ProductQuantitySelector from '@/components/product/ProductQuantitySelector.vue'
import CategoryTree from '@/components/category/CategoryTree.vue'
import { PLACEHOLDER_IMAGE_PATH } from '@/services/imageConstants'
import { debounce } from 'lodash'
import { useCartStore } from '@/stores/cartStore'
import { useFinancialTogglesStore } from '@/stores/financialTogglesStore'
import { useI18n } from 'vue-i18n'
import { productCharacteristicsService } from '@/services/productCharacteristicsService'
import { getDefaultUnit } from '@/utils/unitUtils'
import { locationService } from '@/services/locationService'
import eventBus from '@/utils/eventBus'
// Removing the VueSlider import since it is not installed

const route = useRoute()
const router = useRouter()
const { locale, t } = useI18n() // Adding it here at the top with the other composables

// Ref to store the selected location
const selectedLocation = ref(null)

// Removendo registro do componente VueSlider
const currentPage = ref(1)
const totalPages = ref(1)
const products = ref([])
const loading = ref(true)
const loadingCategories = ref(true)
const error = ref(null)
// We no longer need the categories variable, since we use categoryTree directly
const categoryTree = ref([])
const selectedCategory = ref(null)
const selectedBrands = ref([])
const brands = ref([])
const showMobileFilters = ref(false)
const isMobileFiltersExpanded = ref(false)
const isCategoryExpanded = ref(true)
const isPriceExpanded = ref(true)
const isBrandsExpanded = ref(true)
const priceRange = ref([0, 100000])
const maxPrice = ref(100000)
const totalItems = ref(0)
const itemsPerPage = ref(8)
const sortBy = ref('featured')
const isDragging = ref(false)
const tempPriceRange = ref([0, 1000])
const cartStore = useCartStore()
const togglesStore = useFinancialTogglesStore()
const showToast = ref(false)
const currencySymbol = ref('$')
const showPrices = ref(true) // Controls the price visibility

const currentQuery = computed(() => {
  const q = route.query.q
  return typeof q === 'string' ? q : ''
})
// Watch to update descriptions when the language changes
watch(locale, (newLocale) => {
  if (products.value.length) {
    products.value = products.value.map(product => ({
      ...product,
      description: product[`description_${newLocale}`] || product.description_en || ''
    }))
  }
})

// Watch to update the sortBy value when the master toggle state changes
watch(showPrices, (newValue) => {
  // If the master toggle is disabled and the current sorting is by price,
  // switch to the default sorting (featured)
  if (!newValue && (sortBy.value === 'priceLowHigh' || sortBy.value === 'priceHighLow')) {
    sortBy.value = 'featured'
    fetchSearchProducts()
  }
})

// Function to load the financial settings
const loadFinancialSettings = async () => {
  try {
    const settings = await settingsService.getFinancialSettings()
    console.log('🔧 Financial Settings loaded:', settings)
    currencySymbol.value = settings.currency_symbol
    console.log('💱 Currency Symbol set to:', currencySymbol.value)

    // Loads the toggle states
    togglesStore.loadTogglesFromBackend({
      currency_code_enabled: settings.currency_code_enabled,
      currency_symbol_enabled: settings.currency_symbol_enabled,
      tax_rate_enabled: settings.tax_rate_enabled,
      discount_percentage_enabled: settings.discount_percentage_enabled,
      min_order_value_enabled: settings.min_order_value_enabled,
      free_shipping_threshold_enabled: settings.free_shipping_threshold_enabled,
      shipping_cost_enabled: settings.shipping_cost_enabled,
      master_toggle_enabled: settings.master_toggle_enabled
    })

    // Updates the price visibility based on the master toggle
    showPrices.value = togglesStore.masterToggle
    console.log('Master toggle state:', togglesStore.masterToggle)
    console.log('Show prices:', showPrices.value)
  } catch (error) {
    console.error('Error loading financial settings:', error)
  }
}


const fetchSearchProducts = async () => {
   console.log(`Navegando para a categoria ID: `)
  try {
    loading.value = true


    // Prepares the filter parameters
    const filterParams = {
      search: currentQuery.value,
      //brands: selectedBrands.value,
      page: currentPage.value,
      limit: itemsPerPage.value,
      sortBy: sortBy.value,
      locationId: selectedLocation.value?.id || null  // Adds locationId for dynamic price calculation
    }

    // Adds the price parameters only if the master toggle is enabled
    if (showPrices.value) {
      filterParams.minPrice = priceRange.value[0]
      filterParams.maxPrice = priceRange.value[1]
    }

    console.log('Buscando produtos com os seguintes filtros:', filterParams)


   const [productsResponse, settings] = await Promise.all([
      productService.getProducts(filterParams),
      settingsService.getFinancialSettings()
    ])


    console.log('Resposta da API de produtos:', productsResponse)

    let mapped = productsResponse.items.map(product => ({
      ...product,
      description: product[`description_${locale.value}`] || product.description_en || ''
    }))
    if (showPrices.value) {
      mapped = filterProductsByDisplayedPriceRange(
        mapped,
        priceRange.value[0],
        priceRange.value[1],
      )
    }
    products.value = mapped

    totalItems.value = productsResponse.total
    totalPages.value = Math.ceil(productsResponse.total / itemsPerPage.value)
    currencySymbol.value = settings.currency_symbol

  } catch (err) {
    console.error('Error fetching data:', err)
    error.value = 'Error loading products'
    products.value = []
  } finally {
    loading.value = false
  }
}

const handleSort = () => {
  currentPage.value = 1 // Reset to the first page when the sorting changes
  fetchSearchProducts()
}

const handlePriceRangeChange = () => {
  currentPage.value = 1 // Reset to the first page when the price range changes
  fetchSearchProducts()
}

// Updates the minimum value of the price range
const updateMinPrice = (event) => {
  const minValue = parseInt(event.target.value)
  const maxValue = priceRange.value[1]

  // Ensures the minimum value does not exceed the maximum value
  if (minValue < maxValue) {
    priceRange.value = [minValue, maxValue]
  } else {
    // If the minimum value is greater than the maximum, set it to the maximum - 1
    priceRange.value = [maxValue - 1, maxValue]
    event.target.value = maxValue - 1
  }
}

// Updates the maximum value of the price range
const updateMaxPriceSlider = (event) => {
  const maxValue = parseInt(event.target.value)
  const minValue = priceRange.value[0]

  // Ensures the maximum value is not lower than the minimum value
  if (maxValue > minValue) {
    priceRange.value = [minValue, maxValue]
  } else {
    // If the maximum value is lower than the minimum, set it to the minimum + 1
    priceRange.value = [minValue, minValue + 1]
    event.target.value = minValue + 1
  }
}

// Computed property for filteredProducts
const filteredProducts = computed(() => products.value)

// Computed for the available sorting options
const sortOptions = computed(() => {
  const options = [
    { value: 'featured', label: 'categoryPage.sortBy.featured' },
    { value: 'nameAZ', label: 'categoryPage.sortBy.nameAZ' },
    { value: 'nameZA', label: 'categoryPage.sortBy.nameZA' }
  ]

  // Adds price sorting options only if the master toggle is enabled
  if (showPrices.value) {
    options.splice(1, 0,
      { value: 'priceLowHigh', label: 'categoryPage.sortBy.priceLowHigh' },
      { value: 'priceHighLow', label: 'categoryPage.sortBy.priceHighLow' }
    )
  }

  return options
})

// Computed for the pages to display
const displayedPages = computed(() => {
  const pages = []
  const maxVisiblePages = 5
  let start = Math.max(1, currentPage.value - Math.floor(maxVisiblePages / 2))
  let end = Math.min(totalPages.value, start + maxVisiblePages - 1)

  if (end - start + 1 < maxVisiblePages) {
    start = Math.max(1, end - maxVisiblePages + 1)
  }

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
})

// Computed property to calculate the item range
const itemRange = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value + 1
  const end = Math.min(start + itemsPerPage.value - 1, totalItems.value)
  return { start, end }
})

// Watcher to react to filter changes
watch([selectedBrands, selectedCategory], async () => {
  console.log('Filtros alterados:', {
    selectedBrands: selectedBrands.value,
    selectedCategory: selectedCategory.value
  });

  currentPage.value = 1;

  // If there is a selected category, update the maximum price
  if (selectedCategory.value) {
    console.log('Atualizando preço máximo para a categoria:', selectedCategory.value);
    await updateMaxPrice();
  }

  console.log('Buscando produtos após mudança nos filtros');
  await fetchSearchProducts();
}, { deep: true })

const fetchCategories = async () => {
  try {
    loadingCategories.value = true
    // Fetch categories directly from the backend with a hierarchical structure
    // and only active categories with products
    const hierarchicalCategories = await categoryService.searchCategories('', true)

    // Ensure all categories are collapsed by default
    const setAllCollapsed = (tree) => {
      tree.forEach(category => {
        // Marks the category as collapsed
        category.expanded = false

        // Recursively processes the children
        if (category.children && category.children.length > 0) {
          setAllCollapsed(category.children)
        }
      })
    }

    // Apply the collapse to all categories
    setAllCollapsed(hierarchicalCategories)

    // Use the hierarchical categories with everything collapsed
    categoryTree.value = hierarchicalCategories

    // List categories for debugging
    const listCategoriesWithPath = (tree, path = '') => {
      tree.forEach(category => {
        const currentPath = path ? `${path} > ${category.name}` : category.name
        console.log(`[CategoryPage] Categoria carregada: ${currentPath} (ID: ${category.id}, expanded: ${category.expanded})`)

        if (category.children && category.children.length > 0) {
          listCategoriesWithPath(category.children, currentPath)
        }
      })
    }

    listCategoriesWithPath(categoryTree.value)

    // Removed unnecessary log about root categories
  } catch (err) {
    console.error('Error fetching categories:', err)
    error.value = 'Error loading categories'
    categoryTree.value = []
  } finally {
    loadingCategories.value = false
  }
}

const fetchBrands = async (categoryId = null) => {
  try {
    let response;
    if (categoryId) {
      response = await productService.getBrandsByCategory(categoryId)
    } else {
      response = await productService.getBrands()
    }
    brands.value = response || []
    console.log('Brands loaded:', brands.value)
  } catch (err) {
    console.error('Error fetching brands:', err)
    error.value = 'Error loading brands'
    brands.value = []
  }
}

const updateMaxPrice = async () => {
  if (!showPrices.value) {
    console.log('Preços desabilitados, não atualizando o preço máximo');
    return;
  }

  try {
    const ceil = await productService.getSliderPriceCeil(
      selectedCategory.value,
      selectedBrands.value || [],
      selectedLocation.value?.id || null,
      null,
    );
    maxPrice.value = ceil;
    priceRange.value = [0, ceil];
    console.log(`Preço máximo do slider atualizado para: ${ceil}`);
  } catch (err) {
    console.error('Erro ao buscar preço máximo:', err);
    maxPrice.value = 100000;
    priceRange.value = [0, 100000];
  }
}

const getProductImage = (product) => {
  console.log(`[CategoryPage] Obtendo imagem para produto: ${product.id} - ${product.name}`);
  console.log(`[CategoryPage] Caminho da imagem original: ${product.image}`);
  console.log(`[CategoryPage] FoxPro code: ${product.foxpro_code}`);
  const imageUrl = imageService.getProductImageUrl(product.image, product);
  console.log(`[CategoryPage] Caminho da imagem processado: ${imageUrl}`);
  return imageUrl;
}

const handleImageError = (e) => {
  imageService.handleImageError(e)
}

const selectCategory = async (categoryId) => {
    router.push(`/categories/${categoryId}`)
}

const handleAddToCart = (product, quantity) => {
  // Checks whether the product has characteristics that need to be selected
  if (productCharacteristicsService.hasCharacteristics(product)) {
    // If it has characteristics, redirect to the product details page
    router.push({
      path: `/product/${product.id}`,
      query: { showValidation: 'true' } // Passes a parameter to show the validation
    });
    return;
  }

  // Check whether the product has multiple units of measurement
  const hasMultipleUnits = product.unit_of_measure &&
    product.unit_of_measure.startsWith('{') &&
    product.unit_of_measure.endsWith('}')

  if (hasMultipleUnits) {
    // If it has multiple units, redirect to the product page
    router.push(`/product/${product.id}`)
    return
  }

  // If it has no characteristics and no multiple units, add it straight to the cart
  // Get the default unit of the product
  const defaultUnit = getDefaultUnit(product.unit_of_measure)

  const cartItem = {
    id: product.id,
    name: product.name,
    price: product.price,
    quantity: quantity,
    image: getProductImage(product),
    foxpro_code: product.foxpro_code,
    unit: defaultUnit // Incluir a unidade de medida
  }

  cartStore.addItem(cartItem)
  showSuccessToast()
}

const showSuccessToast = () => {
  showToast.value = true
  setTimeout(() => {
    showToast.value = false
  }, 3000)
}

const handlePageChange = async (page) => {
  // Enables loading only for the product list
  loading.value = true

  try {
    currentPage.value = page
    await fetchSearchProducts()

    // Scroll to the sorting area to make sure it is visible
    setTimeout(() => {
      const sortingElement = document.querySelector('.flex.justify-between.items-center.p-3.border-2.border-black')
      if (sortingElement) {
        const yOffset = -20 // Adjustment to make sure the sorting area stays visible
        const y = sortingElement.getBoundingClientRect().top + window.pageYOffset + yOffset
        window.scrollTo({ top: y, behavior: 'smooth' })
      } else {
        // Fallback to the previous behaviour
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }, 100) // Small delay to make sure the DOM has been updated
  } catch (error) {
    console.error('Erro ao mudar de página:', error)
  } finally {
    // Desativa o loading
    loading.value = false
  }
}

// Function to select the first category
const selectFirstCategory = () => {
  console.log('[CategoryPage] Tentando selecionar a primeira categoria')

  // Checks whether there are categories available
  if (categoryTree.value && categoryTree.value.length > 0) {
    console.log(`[CategoryPage] Selecionando primeira categoria: ${categoryTree.value[0].name} (ID: ${categoryTree.value[0].id})`)
    selectCategory(categoryTree.value[0].id)

    // Scrolls to the selected category
    scrollToCategoryItem(categoryTree.value[0].id)
  } else {
    console.log('[CategoryPage] Nenhuma categoria disponível para seleção')
    // Tries to fetch the categories again
    fetchCategories().then(() => {
      if (categoryTree.value && categoryTree.value.length > 0) {
        selectCategory(categoryTree.value[0].id)

        // Scrolls to the selected category
        scrollToCategoryItem(categoryTree.value[0].id)
      }
    })
  }
}

// Function to select a category by slug
// Function to scroll to the selected item in the category filter
const scrollToCategoryItem = (categoryId) => {
  console.log(`[CategoryPage] Rolando até a categoria selecionada: ${categoryId}`)

  // We increased the delay to make sure the DOM has been fully updated
  setTimeout(() => {
    try {
      // Fetches all elements that may contain the category ID
      const categoryElements = document.querySelectorAll(`[data-category-id="${categoryId}"]`)
      console.log(`[CategoryPage] Encontrados ${categoryElements.length} elementos com data-category-id=${categoryId}`)

      // Finds the most specific element (the category header)
      let selectedElement = null
      categoryElements.forEach(el => {
        if (el.classList.contains('category-header') || el.querySelector('.category-header')) {
          selectedElement = el
          console.log('[CategoryPage] Encontrado elemento header da categoria')
        }
      })

      // If the header was not found, use the first element found
      if (!selectedElement && categoryElements.length > 0) {
        selectedElement = categoryElements[0]
        console.log('[CategoryPage] Usando o primeiro elemento encontrado')
      }

      if (selectedElement) {
        console.log(`[CategoryPage] Elemento da categoria encontrado, rolando até ele`)

        // Finds the category filter container (desktop and mobile)
        const desktopContainer = document.querySelector('.category-filter-container')
        const mobileContainer = document.querySelector('.category-filter-container-mobile')

        // Determines which container is visible
        const container = window.innerWidth >= 1024 ? desktopContainer : mobileContainer

        if (container) {
          console.log(`[CategoryPage] Container encontrado: ${container.className}`)

          // Forces the container to be scrollable
          container.style.overflowY = 'auto'

          // Uses scrollIntoView to scroll to the element
          selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' })

          console.log(`[CategoryPage] Rolagem iniciada para o elemento`)
        } else {
          console.log(`[CategoryPage] Container de filtro de categorias não encontrado`)
        }
      } else {
        console.log(`[CategoryPage] Elemento da categoria com ID ${categoryId} não encontrado no DOM`)
      }
    } catch (error) {
      console.error(`[CategoryPage] Erro ao tentar rolar até a categoria:`, error)
    }
  }, 500) // We increased the delay to 500ms to make sure the DOM has been fully updated
}

const selectCategoryBySlug = async (slug) => {
  console.log(`[CategoryPage] Tentando selecionar categoria por slug: ${slug}`)

  // Waits for the categories to load if they are not loaded yet
  if (loadingCategories.value) {
    console.log(`[CategoryPage] Aguardando carregamento das categorias...`)
    await new Promise(resolve => {
      const checkLoading = setInterval(() => {
        if (!loadingCategories.value) {
          clearInterval(checkLoading)
          resolve()
        }
      }, 100)
    })
  }

  // Checks whether we have categories loaded
  if (categoryTree.value && categoryTree.value.length > 0) {
    console.log(`[CategoryPage] Buscando categoria com ID ${slug} na árvore de categorias`)

    // Function to find the path to the category
    const findPathToCategory = (tree, id, path = []) => {
      for (const node of tree) {
        // Tries with the current node
        const currentPath = [...path, node]
        if (node.id === id) {
          return currentPath
        }
        // Tries with the children
        if (node.children && node.children.length > 0) {
          const foundPath = findPathToCategory(node.children, id, currentPath)
          if (foundPath) return foundPath
        }
      }
      return null
    }

    // Finds the path to the category
    const categoryPath = findPathToCategory(categoryTree.value, slug)

    if (categoryPath) {
      console.log(`[CategoryPage] Categoria encontrada na árvore: ${categoryPath[categoryPath.length-1].name} (ID: ${slug})`)
      console.log(`[CategoryPage] Caminho até a categoria: ${categoryPath.map(c => c.name).join(' > ')}`)

      // Expands all categories along the path
      categoryPath.forEach(cat => {
        cat.expanded = true
      })

      // Updates the category tree to reflect the expansions
      categoryTree.value = [...categoryTree.value]

      // Selects the category
      await selectCategory(slug)

      // Scrolls to the selected item
      scrollToCategoryItem(slug)

      return
    } else {
      console.log(`[CategoryPage] Categoria com ID ${slug} não encontrada na árvore de categorias`)
    }
  }

  // If we got this far, try fetching the category directly from the backend
  try {
    console.log(`[CategoryPage] Tentando buscar a categoria ${slug} diretamente do backend`)

    // Fetch categories with the hierarchical structure including the given category
    await fetchCategories()

    // Try again to find the category in the updated tree
    return selectCategoryBySlug(slug)
  } catch (error) {
    console.error(`[CategoryPage] Erro ao buscar categoria ${slug} do backend:`, error)
  }

  // If we got this far, select the first category as a fallback
  console.log(`[CategoryPage] Selecionando a primeira categoria como fallback`)
  selectFirstCategory()
}


watch(
  () => route.query.q,
  newQ => {
      fetchSearchProducts();
  }
);

onMounted(async () => {
  try {
    console.log('Inicializando a página de search')
    console.log("diego " + currentQuery.value)

    // Loads the selected location from sessionStorage
    selectedLocation.value = locationService.getSelectedLocation()
    console.log('Localização selecionada:', selectedLocation.value)

    // Listener for location changes
    eventBus.on('selected-location-changed', async (newLocation) => {
      console.log('🔄 Localização alterada, atualizando preços:', newLocation)
      selectedLocation.value = newLocation
      // Reloads products with the new prices
      await fetchSearchProducts()
    })

    await loadFinancialSettings()
    await fetchCategories()
    console.log('Categorias carregadas com árvore hierárquica')

   
    // Checks whether the route has a category ID
    if (route.params.slug) {
      console.log('ID de categoria encontrado na rota:', route.params.slug)
      // Selects the category given in the route and expands its parents
     // await selectCategoryBySlug(route.params.slug)
    } else {
      console.log('Nenhum ID de categoria na rota, selecionando a primeira categoria')
      // If there is no ID in the route, select the first category
     // selectFirstCategory()
    }
    if (currentQuery.value) {
      console.log("diego2")
    const response = await productService.getProducts({
      search: currentQuery.value,
      limit: 30 // We raised the limit further to make sure we find results
    })
    console.log("diego2"+JSON.stringify(response,null,2))

    }
    console.log('Categoria selecionada após inicialização:', selectedCategory.value)
    await fetchSearchProducts()
  } catch (err) {
    console.error('Error in initialization:', err)
    error.value = 'Error loading page'
  }
})

// Watch for route changes to update the selected category
watch(
  () => route.params.slug,
  async (newSlug) => {
    console.log('ID de categoria na rota alterado para:', newSlug)

    // Ensures the categories are loaded
    if (loadingCategories.value) {
      await new Promise(resolve => {
        const checkLoading = setInterval(() => {
          if (!loadingCategories.value) {
            clearInterval(checkLoading)
            resolve()
          }
        }, 100)
      })
    }

    // Resets all categories to collapsed
    const resetAllExpanded = (tree) => {
      tree.forEach(category => {
        category.expanded = false
        if (category.children && category.children.length > 0) {
          resetAllExpanded(category.children)
        }
      })
    }
    resetAllExpanded(categoryTree.value)

    // Updates the tree to reflect the changes
    categoryTree.value = [...categoryTree.value]

    if (newSlug) {
      // If there is an ID in the route, select the category and expand its parents
      await selectCategoryBySlug(newSlug)
    } else {
      console.log('ID de categoria removido da rota, selecionando a primeira categoria')
      selectFirstCategory()
    }

    // Forces a product fetch after the category change
    await fetchSearchProducts()
  }
)

// Debounced function that is only called when the user stops dragging
const debouncedFetchProducts = debounce(() => {
  console.log('🎯 Valores atuais:', {
    isDragging: isDragging.value,
    tempRange: tempPriceRange.value,
    currentRange: priceRange.value
  })

  // If NOT dragging AND the values are different, make the request
  if (!isDragging.value &&
      (tempPriceRange.value[0] !== priceRange.value[0] ||
       tempPriceRange.value[1] !== priceRange.value[1])) {
    console.log('🔄 Fazendo requisição após debounce')
    currentPage.value = 1
    fetchSearchProducts()
    tempPriceRange.value = [...priceRange.value]
  } else {
    console.log('❌ Requisição ignorada porque:',
      isDragging.value ? 'ainda está arrastando' : 'valores não mudaram')
  }
}, 500)

const handleRangeStart = () => {
  isDragging.value = true
  console.log('🟢 isDragging:', isDragging.value, '- Início do arrasto')
  tempPriceRange.value = [...priceRange.value]
}

const handleRangeEnd = () => {
  isDragging.value = false
  console.log('🔴 isDragging:', isDragging.value, '- Fim do arrasto')
  if (tempPriceRange.value[0] !== priceRange.value[0] ||
      tempPriceRange.value[1] !== priceRange.value[1]) {
    debouncedFetchProducts()
  }
}

// Watch only for when isDragging changes to false
watch(isDragging, (newValue) => {
  console.log('👀 Watch isDragging:', newValue, {
    tempRange: tempPriceRange.value,
    currentRange: priceRange.value
  })

  if (!newValue &&
      (tempPriceRange.value[0] !== priceRange.value[0] ||
       tempPriceRange.value[1] !== priceRange.value[1])) {
    debouncedFetchProducts()
  }
})

const navigateToProduct = (productId) => {
  router.push(`/product/${productId}`)
}

const formatPrice = (price) => {
  if (price === null || price === undefined) {
    return t('products.priceToConsult')
  }
  const numericPrice = typeof price === 'string' ? parseFloat(price) : Number(price)
  // If the price is NaN or negative, show "À consultar"
  // Price 0.00 is valid and must be displayed normally
  if (isNaN(numericPrice) || numericPrice < 0) {
    return t('products.priceToConsult')
  }
  return `${currencySymbol.value}${numericPrice.toFixed(2)}`
}

</script>

<script>
/* eslint-disable vue/no-dupe-keys */
export default {
  name: 'CategoryPage',
  components: {
    ProductQuantitySelector
  },
  data() {
    return {
      showMobileFilters: false,
      selectedBrands: [],
      priceRange: [0, 100000],
      maxPrice: 100000,
      selectedCategory: null
    }
  },
  methods: {
    handleImageError(e) {
      e.target.src = PLACEHOLDER_IMAGE_PATH
      e.target.onerror = null
    },
    addToCart(product, quantity) {
      console.log('Adding to cart:', product, 'quantity:', quantity)
    },
    selectCategory(categoryId) {
      // If a category is already selected and it is the same one being clicked, do nothing
      if (this.selectedCategory === categoryId) {
        return; // Does not allow unselecting the category
      }

      // Sets the newly selected category
      this.selectedCategory = categoryId;
    }
  }
}
</script>
<style scoped>
input[type="range"]::-webkit-slider-thumb {
  pointer-events: auto;
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  background: #2C2C2C;
  border-radius: 50%;
  cursor: pointer;
}

input[type="range"]::-moz-range-thumb {
  pointer-events: auto;
  width: 16px;
  height: 16px;
  background: #2C2C2C;
  border-radius: 50%;
  cursor: pointer;
  border: none;
}

/* Custom styling for the checkboxes */
input[type="checkbox"] {
  appearance: none;
  -webkit-appearance: none;
  width: 24px;
  height: 24px;
  border: 2px solid rgba(0, 0, 0, 0.7);
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

input[type="checkbox"]:checked {
  background-color: #FBBD1E;
  border-color: #FBBD1E;
}

input[type="checkbox"]:checked::after {
  content: '';
  width: 12px;
  height: 12px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z'/%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
}

.selected-category {
  background-color: rgba(251, 189, 30, 0.1); /* Yellow color with transparency */
}

.selected-category:hover {
  background-color: rgba(251, 189, 30, 0.2); /* Cor amarela um pouco mais escura no hover */
}

.loader-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.loader-spinner {
  border: 4px solid rgba(251, 189, 30, 0.3);
  border-top: 4px solid #FBBD1E;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

.loader-text {
  font-family: 'Archivo', sans-serif;
  font-size: 14px;
  color: #666;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.aspect-square {
  aspect-ratio: 1 / 1;
  object-fit: cover;
}

@media (max-width: 768px) {
  .aspect-square {
    aspect-ratio: 1 / 0.75; /* Slightly smaller ratio for mobile */
  }
}

/* Removing the default select icon on Chrome/Safari */
select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

/* Removing the default select icon on IE/Edge */
select::-ms-expand {
  display: none;
}

/* Making sure the select stays on top of the custom icon */
select {
  position: relative;
  z-index: 1;
  background-color: transparent;
}

/* Style for the select hover */
select:hover {
  border-color: #FFDD00;
}

/* Style for the select focus */
select:focus {
  outline: none;
  border-color: #FFDD00;
  box-shadow: 0 0 0 2px rgba(255, 221, 0, 0.2);
}

/* Add these styles to improve the interaction */
.cursor-pointer:hover {
  transform: translateY(-2px);
  transition: transform 0.2s ease-in-out;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

/* Styles for the price sliders */
input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  height: 5px;
  background: #E0E0E0; /* Back to the original light gray */
  border-radius: 5px;
  background-image: linear-gradient(#F9C349, #F9C349);
  background-repeat: no-repeat;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  height: 22px;
  width: 22px;
  border-radius: 50%;
  background: #F9C349; /* Changed from white to #F9C349 */
  border: 2px solid #F9C349;
  cursor: pointer;
  box-shadow: 0 0 4px 0 rgba(0,0,0,0.3);
  transition: all 0.2s ease;
  position: relative;
  z-index: 10; /* Garantindo que o círculo fique visível */
}

input[type="range"]::-webkit-slider-thumb:hover {
  transform: scale(1.1);
  box-shadow: 0 0 6px 0 rgba(0,0,0,0.4);
}

input[type="range"]::-moz-range-thumb {
  height: 22px;
  width: 22px;
  border-radius: 50%;
  background: #F9C349; /* Changed from white to #F9C349 */
  border: 2px solid #F9C349;
  cursor: pointer;
  box-shadow: 0 0 4px 0 rgba(0,0,0,0.3);
  transition: all 0.2s ease;
  position: relative;
  z-index: 10; /* Garantindo que o círculo fique visível */
}

input[type="range"]::-moz-range-thumb:hover {
  transform: scale(1.1);
  box-shadow: 0 0 6px 0 rgba(0,0,0,0.4);
}

input[type="range"]::-ms-thumb {
  height: 22px;
  width: 22px;
  border-radius: 50%;
  background: #F9C349; /* Changed from white to #F9C349 */
  border: 2px solid #F9C349;
  cursor: pointer;
  box-shadow: 0 0 4px 0 rgba(0,0,0,0.3);
  transition: all 0.2s ease;
  position: relative;
  z-index: 10; /* Garantindo que o círculo fique visível */
}

input[type="range"]::-ms-thumb:hover {
  transform: scale(1.1);
  box-shadow: 0 0 6px 0 rgba(0,0,0,0.4);
}

input[type="range"]::-webkit-slider-runnable-track {
  -webkit-appearance: none;
  box-shadow: none;
  border: none;
  background: #E0E0E0; /* Back to the original light gray */
}

input[type="range"]::-moz-range-track {
  box-shadow: none;
  border: none;
  background: #E0E0E0; /* Back to the original light gray */
}

input[type="range"]::-ms-track {
  box-shadow: none;
  border: none;
  background: #E0E0E0; /* Back to the original light gray */
}

/* Style for the second slider (maximum value) */
input[type="range"]:nth-child(2) {
  background: none;
}

/* Custom style for the automatic spacing */
.mt-auto {
  margin-top: 10%;
}

/* Style to keep the description height fixed */
.description-fixed-height {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  height: 4.5em; /* Fixed height for 3 lines */
  min-height: 4.5em;
  line-height: 1.5em;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 768px) {
  .description-fixed-height {
    height: 2.4em; /* Smaller height for mobile */
    min-height: 2.4em;
    -webkit-line-clamp: 2; /* Only 2 lines on mobile */
    line-clamp: 2;
    line-height: 1.2em;
  }
}

/* Removing the dark gray line styles */

/* Styles for the category filter container */
.category-filter-container {
  background: linear-gradient(145deg, #ffffff, #f8f8f8);
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  padding: 0.5rem;
  transition: all 0.3s ease;
  border: 1px solid rgba(251, 189, 30, 0.1);
  position: relative;
  overflow-y: auto;
  max-height: 70vh; /* Maximum height so the scrollbar shows up when needed */
  scrollbar-width: thin; /* For Firefox */
  scrollbar-color: #FBBD1E #f1f1f1; /* For Firefox */
  scroll-behavior: smooth; /* Rolagem suave */
}

/* Scrollbar styling for WebKit (Chrome, Safari, etc.) */
.category-filter-container::-webkit-scrollbar {
  width: 6px;
}

.category-filter-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.category-filter-container::-webkit-scrollbar-thumb {
  background: #FBBD1E;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.category-filter-container::-webkit-scrollbar-thumb:hover {
  background: #e0a800;
}

.category-filter-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(to bottom, #FBBD1E, rgba(251, 189, 30, 0.3));
  border-radius: 4px 0 0 4px;
}

.category-filter-container-mobile {
  background: linear-gradient(145deg, #ffffff, #f8f8f8);
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  padding: 0.5rem;
  transition: all 0.3s ease;
  border: 1px solid rgba(251, 189, 30, 0.1);
  position: relative;
  overflow-y: auto;
  max-height: 50vh; /* Smaller maximum height for mobile */
  margin-bottom: 1rem;
  scrollbar-width: thin; /* For Firefox */
  scrollbar-color: #FBBD1E #f1f1f1; /* For Firefox */
  scroll-behavior: smooth; /* Rolagem suave */
}

/* Scrollbar styling for WebKit (Chrome, Safari, etc.) */
.category-filter-container-mobile::-webkit-scrollbar {
  width: 6px;
}

.category-filter-container-mobile::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.category-filter-container-mobile::-webkit-scrollbar-thumb {
  background: #FBBD1E;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.category-filter-container-mobile::-webkit-scrollbar-thumb:hover {
  background: #e0a800;
}

.category-filter-container-mobile::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(to bottom, #FBBD1E, rgba(251, 189, 30, 0.3));
  border-radius: 4px 0 0 4px;
}

/* Custom style for the product name with extra thin weight */
.product-name {
  font-weight: 100 !important; /* Forces the thinnest possible weight */
  opacity: 0.7 !important; /* Lowers the opacity further so it looks thinner */
  letter-spacing: 0.03em !important; /* Increases the letter spacing further */
  color: rgba(0, 0, 0, 0.6) !important; /* Lighter color so it looks thinner */
  transform: scale(0.98, 1) !important; /* Comprime ligeiramente na horizontal */
}
</style>
























